<template>
  <div class="station-form-category-container flex flex-col">
    <div
      v-if="urlConviteDoctor && Number(originalDoctor) === Number(doctor)"
      class="flex flex-col justify-center bg-warning p-3 rounded"
    >
      <span class="font-bold">O médico ainda está pendente!</span>
      <a href="" @click="copyConvite" class="text-sm flex justify-center items-center gap-1">
        <v-icon :icon="mdiContentCopy" />
        Clique para copiar o link do convite
      </a>
    </div>

    <v-autocomplete
      v-if="!props.minimal && !props.hideDoctor"
      @update:model-value="setDoctor"
      :items="listDoctorsCompany"
      :model-value="doctor ? String(doctor) : undefined"
      :custom-filter="doctorFilter"
      class="w-full mb-4"
      color="primary20"
      clearable
      label="Nome ou CRM do médico"
      theme="light"
      variant="outlined"
      density="comfortable"
      :disabled="!company || props.disabled"
      hide-details
      data-testid="create-station-doctor-select"
    >
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item?.raw?.title" :subtitle="item?.raw?.subtitle" />
      </template>
    </v-autocomplete>

    <v-row dense class="items-end">
      <div
        data-testid="doctor-infos-row"
        class="w-full flex gap-2"
        :class="props.stacked ? 'flex-col' : 'justify-between'"
      >
        <div v-if="showFormationField" class="w-full">
          <v-autocomplete
            :model-value="isLoading ? 'Buscando...' : props.formation || undefined"
            @update:model-value="setFormation"
            :items="formations"
            label="Formação"
            variant="outlined"
            color="primary20"
            clearable
            density="comfortable"
            item-title="title"
            item-value="value"
            :loading="isLoading"
            :disabled="props.disabled"
          />
        </div>

        <div v-if="props.configurationRepetition == 'no-repeat'" class="w-full">
          <v-autocomplete
            :model-value="isLoading ? 'Buscando...' : props.position || undefined"
            @update:model-value="setPosition"
            :items="positions"
            label="Posição"
            variant="outlined"
            color="primary20"
            clearable
            density="comfortable"
            :loading="isLoading"
            :error-messages="positionErr"
            :disabled="props.disabled"
          />
        </div>

        <div v-if="!props.minimal" class="w-full mb-2">
          <v-autocomplete
            :model-value="isLoading ? 'Buscando...' : props.shift || undefined"
            @update:model-value="setShift"
            :items="shifts"
            label="Turno"
            variant="outlined"
            color="primary20"
            clearable
            density="comfortable"
            :loading="isLoading"
            :disabled="props.disabled"
            :error-messages="props.shiftErr"
          />
        </div>
      </div>

      <div
        data-testid="doctor-infos-row"
        class="w-full flex gap-2"
        :class="props.stacked ? 'flex-col' : 'justify-between'"
      >
        <div class="w-full">
          <v-autocomplete
            :model-value="isLoading ? 'Buscando...' : props.team || undefined"
            @update:model-value="setTeam"
            :items="teams"
            label="Time"
            variant="outlined"
            color="primary20"
            clearable
            density="comfortable"
            :disabled="props.disabled || props.teamLocked"
          />
        </div>

        <div class="w-full">
          <slot></slot>
        </div>
      </div>
    </v-row>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { mdiContentCopy } from '@mdi/js'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import PositionService from '@/Network/Services/PositionService'
import ScheduleShiftService from '@/Network/Services/ScheduleShiftService'
import DsPaymentFormationService from '@/Network/Services/DsPaymentFormationService'

const toast = useToast()
const { notifyError } = useErrorToast()

const props = defineProps({
  doctor: [String, Number],
  company: Number,
  originalDoctor: [String, Number],
  urlConviteDoctor: String,
  minimal: Boolean,
  hideDoctor: { type: Boolean, default: false },
  teamLocked: { type: Boolean, default: false },
  formation: String,
  team: String,
  position: String,
  shift: String,
  schedule: {
    type: Number,
    required: true
  },
  showFormationField: {
    type: Boolean,
    default: false
  },
  positionErr: { type: String, default: undefined },
  shiftErr: { type: String, default: undefined },
  disabled: { type: Boolean, default: false },
  configurationRepetition: { type: String, default: 'no-repeat' },
  /** Uma coluna com largura cheia (Criar plantão em tela cheia no celular, R6). */
  stacked: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:doctor',
  'update:doctorName',
  'update:formation',
  'update:team',
  'update:position',
  'update:shift'
])

const { listDoctorsCompany } = storeToRefs(useDoctorStore())

const formations = ref<{ title: string; value: string | number }[]>([])
const positions = ref<{ title: string; value: string | number }[]>([])
const shifts = ref<{ title: string; value: string | number }[]>([])
const isLoading = ref(false)
const teams = ['Interno', 'Externo']

const doctorFilter = (itemTitle, queryText, item) => {
  const name = item.raw.title.toLowerCase()
  const crm = item.raw.subtitle.toLowerCase()
  const searchText = queryText.toLowerCase()
  return name.includes(searchText) || crm.includes(searchText)
}

const fetchDataBySchedule = async (scheduleId: number) => {
  if (!scheduleId) {
    formations.value = []
    positions.value = []
    shifts.value = []
    return
  }

  try {
    isLoading.value = true
    const [formationRes, positionRes, shiftRes] = await Promise.all([
      new DsPaymentFormationService().getByScheduleId(scheduleId),
      new PositionService().getByScheduleId(scheduleId),
      new ScheduleShiftService().getByScheduleId(scheduleId)
    ])

    formations.value = formationRes.data.map((item) => ({
      title: item.name,
      value: item.id
    }))

    positions.value = positionRes.data.map((item) => ({
      title: item.name,
      value: String(item.id)
    }))

    shifts.value = shiftRes.data.map((item) => ({
      title: item.name,
      value: String(item.id)
    }))
  } catch (e) {
    // Fallback específico do lote (formação + posição + turno): a mensagem
    // do ServiceError de quem falhou tem prioridade.
    notifyError(e, 'Não foi possível carregar formações, posições e turnos da escala')
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.schedule,
  (newVal) => {
    if (newVal) {
      fetchDataBySchedule(Number(newVal))
    } else {
      formations.value = []
      positions.value = []
      shifts.value = []
    }
  },
  { immediate: true }
)

const setFormation = (val: string) => emit('update:formation', val)
const setTeam = (val: string) => emit('update:team', val)
const setPosition = (val: string) => emit('update:position', val)
const setShift = (val: string) => emit('update:shift', val)

const copyConvite = async (e: Event) => {
  e.preventDefault()
  await navigator.clipboard.writeText(props.urlConviteDoctor || '')
  toast.success('Link copiado com sucesso')
}

const setDoctor = (doctorId: string) => {
  if (doctorId) {
    const selected = listDoctorsCompany.value.find((d) => d.value === doctorId)
    emit('update:doctor', Number(doctorId))
    emit('update:doctorName', selected?.title || '')
  } else {
    emit('update:doctor', null)
    emit('update:doctorName', '')
  }
}
</script>
